//@flow
import React, { PureComponent } from 'react';
import { Marker, Popup } from 'react-leaflet';
import {
    Text,
    Box,
    Stack,
    Columns,
    Column,
    Button as NeoButton,
    Chip as NeoChip
} from '@locus-taxy/neo-ui';
import { type ThemeType } from 'react-css-themr';
import { Select } from 'common/components/core/select';
import enhancedFieldFactory from 'common/components/core/form/EnhancedFieldFactory';
import { MasterOutlet } from 'common/models/internal/MasterOutlet';
import { type TFunction } from 'react-i18next';
import type { OutletMarketLtLngTpye } from './TerritoryPlanningContainer';
import { Team } from 'common/models/internal/Team';
import type { LatLng } from 'common/models/internal/LatLng';

const EnhancedSelect = enhancedFieldFactory({
    FieldComponent: Select,
    attachPopover: false
});
type OutletPopupState = {
    selectedDistributor: ?string,
    selectMenuOpen: boolean
};

type OutletMarkerProps = {
    latLngObject: ?OutletMarketLtLngTpye,
    t: TFunction,
    validateTeamAndHomebase: () => boolean,
    unassignOutletInProgress: boolean,
    isOutletUpdateInProgress: boolean,
    isSorting: boolean,
    outletAssigned: boolean,
    selectedTeam: ?Team,
    selectedHomebase: ?string,
    sortOutlet: (clientId: string, outletId: string, homebase: string) => void,
    clientId: string,
    updateOutlet: (
        { clientId: string, outletId: string },
        oldOutlet: MasterOutlet,
        updatedOutlet: MasterOutlet,
        unssigned: boolean
    ) => void,
    allDistributors: ?Array<string>,
    theme: ThemeType,
    position: ?LatLng
};
class OutletPopup extends React.Component<OutletMarkerProps, OutletPopupState> {
    constructor(props) {
        super(props);
        this.state = {
            selectedDistributor: null,
            selectMenuOpen: false
        };
    }

    componentDidUpdate(prevProps) {
        const { latLngObject } = prevProps;
        const { latLngObject: currentLatLngObject } = this.props;
        if (latLngObject.outlet.id !== currentLatLngObject.outlet.id) {
            this.setState({
                selectedDistributor: null
            });
        }
    }

    getDistributorOptions = () => {
        const { allDistributors, latLngObject } = this.props;
        const {
            distributor: outletDistributor
        } = latLngObject.outlet.customProperties;
        const availableDistributors = allDistributors.filter(distributor => {
            return distributor !== outletDistributor;
        });
        return availableDistributors.map(value => {
            return {
                label: value,
                value
            };
        });
    };
    onDistributorSelect = distributor => {
        this.setState({
            selectedDistributor: distributor.value,
            selectMenuOpen: false
        });
    };

    onSelectClick = () => {
        const { selectMenuOpen } = this.state;
        this.setState({
            selectMenuOpen: !selectMenuOpen
        });
    };

    sortOutlet = () => {
        const {
            clientId,
            sortOutlet,
            latLngObject,
            selectedHomebase,
            validateTeamAndHomebase
        } = this.props;
        const isInvalid = validateTeamAndHomebase();
        if (isInvalid) {
            return;
        }
        const outletId = latLngObject.outlet.id;

        sortOutlet(clientId, outletId, selectedHomebase?.value).then(
            sortedDistributor => {
                const selectedRoute = sortedDistributor.zoneName;
                this.setState({
                    selectedDistributor: selectedRoute
                });
            }
        );
    };

    unassignOutlet = () => {
        const { latLngObject, updateOutlet, clientId } = this.props;
        const oldOutlet = MasterOutlet.fromJS(latLngObject.outlet);
        const oldCustomProperties = oldOutlet.customProperties;
        const updatedCustomProperties = oldCustomProperties.update(
            'distributor',
            () => null
        );
        const updatedOutlet = oldOutlet.setCustomProperties(
            updatedCustomProperties
        );
        const params = { clientId, outletId: oldOutlet.id };
        updateOutlet(params, oldOutlet, updatedOutlet, true);
    };

    handleDistributorChange = () => {
        const { selectedDistributor } = this.state;
        const { latLngObject, updateOutlet, clientId } = this.props;
        const oldOutlet = MasterOutlet.fromJS(latLngObject.outlet);
        const oldCustomProperties = oldOutlet.customProperties;
        const updatedCustomProperties = oldCustomProperties.update(
            'distributor',
            () => selectedDistributor
        );
        const updatedOutlet = oldOutlet.setCustomProperties(
            updatedCustomProperties
        );
        const params = { clientId, outletId: oldOutlet.id };
        updateOutlet(params, oldOutlet, updatedOutlet, false);
    };

    render() {
        const {
            latLngObject,
            theme,
            outletAssigned,
            isSorting,
            isOutletUpdateInProgress,
            unassignOutletInProgress,
            t
        } = this.props;
        const { selectedDistributor, selectMenuOpen } = this.state;
        return (
            <Box p={2} width="360px">
                <Stack space="small">
                    <Columns alignY="center" space="small">
                        <Column width="content">
                            <Text fontSize={2} fontWeight={500}>
                                {latLngObject.outletName ||
                                    latLngObject.outletId}
                            </Text>
                        </Column>
                        <Column width="content">
                            <NeoChip
                                palette={
                                    latLngObject.distributor
                                        ? 'primary'
                                        : 'neutral'
                                }
                                tone={
                                    latLngObject.distributor ? 'dark' : 'light'
                                }
                                sx={{ backgroundColor: 'black' }}
                            >
                                <Text fontSize={2} fontWeight={500}>
                                    {latLngObject.distributor
                                        ? latLngObject.distributor
                                        : t('territory.unallocated')}
                                </Text>
                            </NeoChip>
                        </Column>
                    </Columns>

                    <Columns space="small" alignY="center" align="center">
                        <Column width="fluid">
                            <Box onClick={this.onSelectClick}>
                                <EnhancedSelect
                                    theme={theme}
                                    isClearable
                                    menuIsOpen={selectMenuOpen}
                                    value={selectedDistributor}
                                    placeholder={t('territory.selectedDist')}
                                    options={this.getDistributorOptions()}
                                    onChange={this.onDistributorSelect}
                                />
                            </Box>
                        </Column>
                    </Columns>
                    <Columns alignY="center" space="small" align="center">
                        <Column>
                            <NeoButton
                                isLoading={isSorting}
                                appearance="outline"
                                palette="primary"
                                onClick={this.sortOutlet}
                            >
                                {t('territory.sort')}
                            </NeoButton>
                        </Column>
                        <Column>
                            <NeoButton
                                isLoading={isOutletUpdateInProgress}
                                disabled={!selectedDistributor}
                                appearance="solid"
                                palette="primary"
                                onClick={this.handleDistributorChange}
                            >
                                {t('territory.assign')}
                            </NeoButton>
                        </Column>
                        {outletAssigned ? (
                            <Column>
                                <NeoButton
                                    isLoading={unassignOutletInProgress}
                                    appearance="solid"
                                    palette="primary"
                                    onClick={this.unassignOutlet}
                                >
                                    {t('territory.unassign')}
                                </NeoButton>
                            </Column>
                        ) : null}
                    </Columns>
                </Stack>
            </Box>
        );
    }
}

export default class OutletMarker extends PureComponent<OutletMarkerProps> {
    render() {
        const { latLngObject, ...rest } = this.props;
        const { lat, lng } = latLngObject;
        return (
            <Marker position={{ lat, lng }} icon={latLngObject.icon}>
                <Popup>
                    <OutletPopup latLngObject={latLngObject} {...rest} />
                </Popup>
            </Marker>
        );
    }
}
